import Carregando from '../components/Carregando';
import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import SecaoFormulario from '../components/SecaoFormulario';
import PageHeader from '../components/PageHeader';

const statusConfig = {
  ativo:     { label: 'Ativo',     color: 'success' },
  pausado:   { label: 'Pausado',   color: 'warning' },
  encerrado: { label: 'Encerrado', color: 'error'   },
  prospecto: { label: 'Prospecto', color: 'default' },
};

export default function Clientes() {
  const { temAcesso } = useAuth();
  const podeCadastrar = temAcesso('clientes');

  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');

  const [abrirModalCliente, setAbrirModalCliente] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [formCliente, setFormCliente] = useState({
    unidade: '', gerente_unidade: '', coordenador_projeto: '', codigo_agenda: '',
  });

  const carregarClientes = () => {
    api.get('/clientes/').then((res) => {
      setClientes(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    carregarClientes();
  }, []);

  const handleAbrirEditarCliente = (cliente) => {
    setClienteEditando(cliente);
    setFormCliente({
      unidade: cliente.unidade || '',
      gerente_unidade: cliente.gerente_unidade || '',
      coordenador_projeto: cliente.coordenador_projeto || '',
      codigo_agenda: cliente.codigo_agenda || '',
    });
    setErro('');
    setAbrirModalCliente(true);
  };

  const handleSalvarCliente = async () => {
    setErro('');
    try {
      await api.patch(`/clientes/${clienteEditando.id}/`, formCliente);
      setSucesso('Informações do cliente atualizadas com sucesso!');
      setAbrirModalCliente(false);
      carregarClientes();
    } catch (err) {
      const detalhe = err?.response?.data;
      const primeiroCampo = detalhe && typeof detalhe === 'object' ? Object.values(detalhe)[0] : null;
      setErro(primeiroCampo || 'Erro ao atualizar informações do cliente.');
    }
  };

  const filtrados = clientes.filter((c) =>
    c.nome?.toLowerCase().includes(busca.toLowerCase()) ||
    c.cidade?.toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <Box>
      <PageHeader titulo="Clientes" />

      <TextField
        fullWidth
        placeholder="Buscar por nome ou cidade..."
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />

      {sucesso && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>
          {sucesso}
        </Alert>
      )}

      {loading ? (
        <Carregando />
      ) : filtrados.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" textAlign="center">
              Nenhum cliente encontrado.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {filtrados.map((c) => {
            const st = statusConfig[c.status_contrato] || statusConfig.ativo;
            return (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Card
                  sx={{
                    height: '100%', position: 'relative', borderRadius: 3,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(15,23,42,0.12)' },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            backgroundColor: '#13375F',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <BusinessIcon sx={{ color: 'white' }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {c.nome}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {c.cidade && c.estado
                              ? `${c.cidade} — ${c.estado}`
                              : 'Localização não informada'}
                          </Typography>
                        </Box>
                      </Box>
                      {podeCadastrar && (
                        <IconButton
                          size="small"
                          onClick={() => handleAbrirEditarCliente(c)}
                          sx={{
                            position: 'absolute', top: 14, right: 14,
                            backgroundColor: '#f8f9fa',
                            '&:hover': { backgroundColor: '#eef0f3' },
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Chip label={st.label} color={st.color} size="small" />
                      <Typography variant="caption" color="text.secondary">
                        {c.segmento || 'Segmento não informado'}
                      </Typography>
                    </Box>

                    {c.codigo_agenda && (
                      <Chip label={`Código: ${c.codigo_agenda}`} size="small" variant="outlined" sx={{ mb: 1 }} />
                    )}

                    {c.gerente_unidade && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        Gerente: {c.gerente_unidade}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Modal Editar Cliente */}
      <Dialog open={abrirModalCliente} onClose={() => setAbrirModalCliente(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Editar informações — {clienteEditando?.nome}</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
          Atualize os dados de unidade e responsáveis deste cliente
        </Typography>
        <DialogContent>
          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

          <SecaoFormulario titulo="Dados da unidade" icone={<BusinessIcon sx={{ fontSize: 17, color: '#13375F' }} />} cor="#13375F">
            <Grid item xs={12}>
              <TextField
                fullWidth label="Unidade de negócio"
                value={formCliente.unidade}
                onChange={(e) => setFormCliente({ ...formCliente, unidade: e.target.value })}
                placeholder="Ex: Atlas"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Código na Agenda"
                value={formCliente.codigo_agenda}
                onChange={(e) => setFormCliente({ ...formCliente, codigo_agenda: e.target.value })}
                placeholder="Ex: RCH, BET, SKA"
                helperText="Código curto usado pra identificar esse cliente na Agenda"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Gerente da unidade"
                value={formCliente.gerente_unidade}
                onChange={(e) => setFormCliente({ ...formCliente, gerente_unidade: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Coordenador do projeto"
                value={formCliente.coordenador_projeto}
                onChange={(e) => setFormCliente({ ...formCliente, coordenador_projeto: e.target.value })}
              />
            </Grid>
          </SecaoFormulario>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbrirModalCliente(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvarCliente} sx={{ backgroundColor: '#13375F' }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}